/**
 * useZoneAutocomplete — zone/geo autocomplete hook for Cibles.
 *
 * Extracted from the inline state in EstablishmentAddForm (edit mode) and the
 * shared zone state in TargetsPanel. Both callers manage the same pattern:
 * debounced API calls to /locations/autocomplete, a selected GeoSuggestion, and
 * a reset function.
 */
import { useState, useCallback, useRef } from 'react';
import { TARGET_LIMITS } from '@traaker/shared';
import { api } from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GeoSuggestion {
  label: string;
  type: string;
  countryCode: string;
  cityName?: string;
  regionName?: string;
  postcode?: string;
  latitude?: number;
  longitude?: number;
}

export interface ZoneAutocompleteState {
  query: string;
  suggestions: GeoSuggestion[];
  attribution: string;
  selected: GeoSuggestion | null;
  onQueryChange: (v: string) => void;
  onSelect: (s: GeoSuggestion) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Zone autocomplete state and handlers.
 *
 * `debounceMs` defaults to 1 000 ms (the shared add-dialog uses a 1-second
 * debounce so the per-keystroke API budget is not exhausted; EstablishmentAddForm
 * edit mode uses 300 ms for a snappier feel — pass the shorter value there).
 */
export function useZoneAutocomplete(debounceMs = 1000): ZoneAutocompleteState & { reset: () => void } {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<GeoSuggestion[]>([]);
  const [attribution, setAttribution] = useState('© Geoapify');
  const [selected, setSelected] = useState<GeoSuggestion | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const lastFetchedQueryRef = useRef<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchZone = useCallback(async (q: string) => {
    if (q.length < TARGET_LIMITS.geoAutocompleteMinLength) { setSuggestions([]); return; }
    // Skip if the query hasn't changed from the last fetch.
    if (q === lastFetchedQueryRef.current) return;
    lastFetchedQueryRef.current = q;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    try {
      const data = await api.get(`/locations/autocomplete?q=${encodeURIComponent(q)}&lang=fr`) as { suggestions: GeoSuggestion[]; attribution: string };
      setSuggestions(data.suggestions);
      setAttribution(data.attribution);
    } catch { setSuggestions([]); }
  }, []);

  const onQueryChange = useCallback((v: string) => {
    setQuery(v); setSelected(null);
    // Debounce that resets on every keystroke.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchZone(v), debounceMs);
  }, [fetchZone, debounceMs]);

  const onSelect = useCallback((s: GeoSuggestion) => {
    setSelected(s); setQuery(s.label); setSuggestions([]);
  }, []);

  const reset = useCallback(() => {
    setQuery(''); setSelected(null); setSuggestions([]);
    lastFetchedQueryRef.current = '';
  }, []);

  return { query, suggestions, attribution, selected, onQueryChange, onSelect, reset };
}
